import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "@/store/hooks";
import { shiftApi } from "@/features/shift/api";
import { shiftRequestApi } from "@/features/shift-request/api";
import type { Shift } from "@/features/shift/types";
import type { ShiftRequest } from "@/features/shift-request/types";
import { groupByDate, pendingDateSet } from "../calendar";

export function useMyShiftData(rangeStart: string, rangeEnd: string) {
  const userId = useAppSelector((s) => s.user.userId) as string | undefined;
  const [schedules, setSchedules] = useState<Shift[]>([]);
  const [requests, setRequests] = useState<ShiftRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!rangeStart || !rangeEnd) return;
    setLoading(true);
    // schedules
    try {
      const sch = await shiftApi.getMyShifts(rangeStart, rangeEnd);
      setSchedules(Array.isArray(sch) ? sch : []);
    } catch (err) {
      // 后端契约问题大声失败 + dump shape，便于直接拿日志找后端
      console.error("[my-shift] schedules load failed", { rangeStart, rangeEnd, err });
      setSchedules([]);
    }
    // my requests（无 userId 直接置空，不算错误）
    if (userId) {
      try {
        const reqRes: any = await shiftRequestApi.searchShiftRequests({
          filters: { requester_id: { type: "string", operator: "equals", value: userId } },
          // 不传 columns 只回最小字段集 → DayDetail pending 卡的 payload/reason、
          // pendingDateSet/computeLockState 的 scheduleId/requestType 会缺失
          columns: "id requestType status reason payload scheduleId",
          orderBy: "-createdAt",
          page: 1,
          pageSize: 200, // 日历视图一次性加载当前 range 的请求；月视图场景 200 足够
        });
        setRequests(Array.isArray(reqRes?.results) ? reqRes.results : []);
      } catch (err) {
        console.error("[my-shift] my requests load failed", { userId, err });
        setRequests([]);
      }
    } else {
      setRequests([]);
    }
    setLoading(false);
  }, [rangeStart, rangeEnd, userId]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  const byDate = groupByDate(schedules);
  const pendingDates = pendingDateSet(schedules, requests);

  return { schedules, requests, byDate, pendingDates, loading, refetch: fetchData };
}
